import React, { useMemo, useContext } from 'react'
import { Switch, Drawer, PageHelperText } from '@/components/ui'
import { HolderOutlined } from '@ant-design/icons'
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { RegistrationField } from '../types'

interface ManageColumnsDrawerProps {
  open: boolean
  onClose: () => void
  registrationFields: RegistrationField[]
  visibleColumns: Record<string, boolean>
  onToggleColumn: (columnKey: string) => void
  columnOrder: string[]
  onReorderColumns: (nextOrder: string[]) => void
}

export default function ManageColumnsDrawer({
  open,
  onClose,
  registrationFields,
  visibleColumns,
  onToggleColumn,
  columnOrder,
  onReorderColumns,
}: ManageColumnsDrawerProps) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

  const items = useMemo(() => {
    const fieldIds = registrationFields.map((f) => f.identifier)
    const base = columnOrder.length ? columnOrder : [...fieldIds, 'created_at']
    const allKeys = Array.from(new Set([...base, ...fieldIds, 'created_at']))
    return allKeys
  }, [columnOrder, registrationFields])

  const handleDragEnd = (event: any) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = items.indexOf(active.id)
    const newIndex = items.indexOf(over.id)
    const reordered = arrayMove(items, oldIndex, newIndex)
    onReorderColumns(reordered)
  }

  const RowContext = React.createContext<{ setActivatorNodeRef?: (el: HTMLElement | null) => void; listeners?: any }>({})

  const DragHandle: React.FC = () => {
    const { setActivatorNodeRef, listeners } = useContext(RowContext)
    return (
      <button
        type="button"
        ref={setActivatorNodeRef}
        className="p-1 text-gray-500 hover:text-gray-700 cursor-move rounded border border-transparent hover:border-gray-200 bg-transparent"
        aria-label="Drag to reorder"
        {...listeners}
      >
        <HolderOutlined />
      </button>
    )
  }

  const SortableRow: React.FC<{ id: string; label: string; toggler: React.ReactNode }> = ({ id, label, toggler }) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id })
    const style: React.CSSProperties = {
      transform: CSS.Translate.toString(transform),
      transition,
      ...(isDragging ? { zIndex: 5, position: 'relative', background: 'rgba(0,0,0,0.02)' } : {})
    }
    const ctxValue = useMemo(() => ({ setActivatorNodeRef, listeners }), [setActivatorNodeRef, listeners])
    return (
      <RowContext.Provider value={ctxValue}>
        <div ref={setNodeRef} style={style} {...attributes} className="flex items-center justify-between py-2">
          <div className="flex items-center gap-2">
            <DragHandle />
            <span className="text-sm">{label}</span>
          </div>
          {toggler}
        </div>
      </RowContext.Provider>
    )
  }

  return (
    <Drawer
      title="Show Columns"
      onClose={onClose}
      open={open}
      onSubmit={onClose}
      submitText="Show Columns"
    >
      <div className="mb-6">
        <PageHelperText>
          Toggle on for columns to appear on the attendee list.
        </PageHelperText>
      </div>
      <div className="space-y-4">
        <h3 className="font-semibold text-sm mb-3">Registration Fields</h3>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            <div className="space-y-1">
              {items.map((key) => {
                const field = registrationFields.find((f) => f.identifier === key)
                const label = field
                  ? field.label
                  : key === 'created_at'
                    ? 'Registration Date'
                    : key === 'status'
                      ? 'Status'
                      : key === 'badge_print_status'
                        ? 'Badge Print'
                      : key
                return (
                  <SortableRow
                    key={key}
                    id={key}
                    label={label}
                    toggler={
                      <Switch
                        checked={visibleColumns[key] ?? false}
                        onChange={() => onToggleColumn(key)}
                      />
                    }
                  />
                )
              })}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </Drawer>
  )
}
